import type { PeriodId } from "@/grid/core/types";

export type StatusColor = "green" | "yellow" | "red";

export interface StatusThresholds {
  green: number;
  yellow: number;
}

export const DEFAULT_THRESHOLDS: StatusThresholds = {
  green: 99,
  yellow: 60,
};

/** Missing warehouse actuals display and score as zero. */
export function resolveActual(actual: number | null | undefined): number {
  return actual ?? 0;
}

/** Pace % from raw actual and compare target (not display-rounded amounts). */
export function pacePercentage(actual: number, compareTarget: number): number {
  if (compareTarget <= 0) return actual > 0 ? 100 : 0;
  return Math.round((actual / compareTarget) * 100);
}

/** Completed workdays this week (Mon=0 … Fri=4; weekends=5). */
export function getWorkdayOfWeek(asOf = new Date()): number {
  const day = asOf.getDay();
  if (day === 0 || day === 6) return 5;
  return day - 1;
}

export function getProratedWeeklyTarget(weeklyTarget: number, asOf = new Date()): number {
  return (weeklyTarget / 5) * getWorkdayOfWeek(asOf);
}

export function getMtdPacingTarget(monthlyTarget: number, asOf = new Date()): number {
  const year = asOf.getFullYear();
  const month = asOf.getMonth();
  const dayOfMonth = asOf.getDate();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  if (daysInMonth <= 0) return monthlyTarget;
  return (monthlyTarget / daysInMonth) * dayOfMonth;
}

export function getQtdPacingTarget(quarterlyTarget: number, asOf = new Date()): number {
  const year = asOf.getFullYear();
  const quarterStartMonth = Math.floor(asOf.getMonth() / 3) * 3;
  const quarterStart = new Date(year, quarterStartMonth, 1);
  const nextQuarter = new Date(year, quarterStartMonth + 3, 1);
  const daysInQuarter = Math.round((nextQuarter.getTime() - quarterStart.getTime()) / 86_400_000);
  const daysElapsed = Math.round((asOf.getTime() - quarterStart.getTime()) / 86_400_000) + 1;
  if (daysInQuarter <= 0) return quarterlyTarget;
  return (quarterlyTarget / daysInQuarter) * Math.max(1, daysElapsed);
}

/** Matches bb-grid: zero compare target is treated as on track. */
export function getStatusColor(
  actual: number,
  compareTarget: number,
  thresholds: StatusThresholds = DEFAULT_THRESHOLDS,
): StatusColor {
  if (compareTarget === 0) return "green";

  const pct = (actual / compareTarget) * 100;
  if (pct >= thresholds.green) return "green";
  if (pct >= thresholds.yellow) return "yellow";
  return "red";
}

export function getWtdStatus(
  actual: number | null,
  weeklyTarget: number | null,
  thresholds: StatusThresholds = DEFAULT_THRESHOLDS,
  asOf = new Date(),
): StatusColor {
  const resolvedActual = resolveActual(actual);
  if (weeklyTarget == null) return "green";
  const proratedTarget = getProratedWeeklyTarget(weeklyTarget, asOf);
  return getStatusColor(resolvedActual, proratedTarget, thresholds);
}

export function getPacingStatus(
  actual: number | null,
  target: number | null,
  pacingTarget: number | null,
  thresholds: StatusThresholds = DEFAULT_THRESHOLDS,
): StatusColor {
  const resolvedActual = resolveActual(actual);
  const compareTarget = pacingTarget ?? target ?? 0;
  return getStatusColor(resolvedActual, compareTarget, thresholds);
}

/** Period-aware status — mirrors bb-grid TeamCard / scorecard logic. */
export function getCellStatusForPeriod(
  periodId: PeriodId,
  actual: number | null,
  target: number | null,
  pacingTarget: number | null,
  thresholds: StatusThresholds = DEFAULT_THRESHOLDS,
  asOf = new Date(),
): StatusColor {
  switch (periodId) {
    case "wtd":
      return getWtdStatus(actual, target, thresholds, asOf);
    case "lastWeek":
      return getStatusColor(resolveActual(actual), target ?? 0, thresholds);
    case "mtd":
    case "qtd":
      return getPacingStatus(actual, target, pacingTarget, thresholds);
    default:
      return getPacingStatus(actual, target, pacingTarget, thresholds);
  }
}

/** @deprecated Prefer getCellStatusForPeriod for grid cells. */
export function getCellStatusColor(
  actual: number | null,
  pacingTarget: number | null,
  thresholds: StatusThresholds = DEFAULT_THRESHOLDS,
): StatusColor {
  return getPacingStatus(actual, null, pacingTarget, thresholds);
}
